import { c as _c } from "react/compiler-runtime";
import { useLastOrganisation, useOrganisationScope } from '@repo/features/organisations';
import type { drops } from '@repo/form/drops';
import type { OrganisationScope } from '@repo/upfetch';
import { getScopeOrganisationId } from '@repo/upfetch/core';
import { useEffect, useRef } from 'react';
export type OrganisationPreferenceOrigin = 'hydration' | 'selection';

/** A new Load's Organisation: the scope's, else the Last-used Organisation. */
export const getDefaultLoadOrganisationId = ({
  lastOrganisationId,
  scope
}: {
  lastOrganisationId: number | undefined;
  scope: OrganisationScope;
}) => getScopeOrganisationId(scope) ?? lastOrganisationId;
export const resolveFormOrganisationId = ({
  initialDraftOrganisationId,
  isExistingDraft = false,
  lastOrganisationId,
  organisationId,
  scope
}: {
  initialDraftOrganisationId?: null | number;
  isExistingDraft?: boolean;
  lastOrganisationId: number | undefined;
  organisationId: null | number;
  scope: OrganisationScope;
}): null | number => {
  // A loaded draft starts in its saved organisation. The Organisation scope and the Last-used
  // Organisation must not retarget its company, contact, or saved locations, but an explicit form
  // selection wins.
  if (isExistingDraft) return organisationId ?? initialDraftOrganisationId ?? null;
  if (scope.kind === 'all' && organisationId != null) return organisationId;
  return getDefaultLoadOrganisationId({
    lastOrganisationId,
    scope
  }) ?? null;
};
export const shouldRememberFormOrganisation = ({
  organisationId,
  origin,
  scope
}: {
  organisationId: null | number;
  origin: OrganisationPreferenceOrigin;
  scope: OrganisationScope;
}): boolean => origin === 'selection' && scope.kind === 'all' && organisationId != null;
export const nextRememberedOrganisationId = ({
  isExistingDraft = false,
  lastOrganisationId,
  organisationId,
  origin,
  scope
}: {
  isExistingDraft?: boolean;
  lastOrganisationId: number | undefined;
  organisationId: null | number;
  origin: OrganisationPreferenceOrigin;
  scope: OrganisationScope;
}): number | undefined => {
  if (isExistingDraft && origin === 'hydration') return lastOrganisationId;
  if (shouldRememberFormOrganisation({
    organisationId,
    origin,
    scope
  }) && organisationId != null) {
    return organisationId;
  }
  return lastOrganisationId;
};
export function useOrganisationFormState(t0) {
  const $ = _c(46);
  const {
    companyId,
    contactId,
    initialDraftOrganisationId,
    isExistingDraft,
    organisationId,
    routeDrops,
    setFieldValue,
    setRouteDrops
  } = t0;
  const {
    scope
  } = useOrganisationScope();
  const {
    lastOrganisationId,
    rememberOrganisation
  } = useLastOrganisation();
  const isAllOrganisationsSelected = scope.kind === "all";
  const canSelectOrganisation = isAllOrganisationsSelected || isExistingDraft;
  let t1;
  if ($[0] !== initialDraftOrganisationId || $[1] !== isExistingDraft || $[2] !== organisationId || $[3] !== scope) {
    t1 = isExistingDraft ? organisationId ?? initialDraftOrganisationId ?? null : getScopeOrganisationId(scope) ?? organisationId;
    $[0] = initialDraftOrganisationId;
    $[1] = isExistingDraft;
    $[2] = organisationId;
    $[3] = scope;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const effectiveOrganisationId = t1;
  const inheritedOrganisationId = effectiveOrganisationId ?? undefined;
  const localOrgIdOverride = (isExistingDraft ? organisationId ?? initialDraftOrganisationId : undefined) ?? (isAllOrganisationsSelected ? organisationId ?? undefined : undefined);
  const hasOrganisationSelection = effectiveOrganisationId !== null && effectiveOrganisationId !== undefined;
  const organisationPreferenceOrigin = useRef("hydration");
  let t2;
  let t3;
  if ($[5] !== initialDraftOrganisationId || $[6] !== isExistingDraft || $[7] !== lastOrganisationId || $[8] !== organisationId || $[9] !== scope || $[10] !== setFieldValue) {
    t2 = () => {
      const nextOrganisationId = resolveFormOrganisationId({
        initialDraftOrganisationId,
        isExistingDraft,
        lastOrganisationId,
        organisationId,
        scope
      });
      if (nextOrganisationId !== organisationId) {
        setFieldValue("organisation_id", nextOrganisationId);
      }
    };
    t3 = [initialDraftOrganisationId, isExistingDraft, lastOrganisationId, organisationId, scope, setFieldValue];
    $[5] = initialDraftOrganisationId;
    $[6] = isExistingDraft;
    $[7] = lastOrganisationId;
    $[8] = organisationId;
    $[9] = scope;
    $[10] = setFieldValue;
    $[11] = t2;
    $[12] = t3;
  } else {
    t2 = $[11];
    t3 = $[12];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[13] !== isExistingDraft || $[14] !== lastOrganisationId || $[15] !== organisationId || $[16] !== rememberOrganisation || $[17] !== scope) {
    t4 = () => {
      const rememberedOrganisationId = nextRememberedOrganisationId({
        isExistingDraft,
        lastOrganisationId,
        organisationId,
        origin: organisationPreferenceOrigin.current,
        scope
      });
      if (rememberedOrganisationId != null && rememberedOrganisationId !== lastOrganisationId) {
        rememberOrganisation(rememberedOrganisationId);
      }
      organisationPreferenceOrigin.current = "selection";
    };
    t5 = [isExistingDraft, lastOrganisationId, organisationId, rememberOrganisation, scope];
    $[13] = isExistingDraft;
    $[14] = lastOrganisationId;
    $[15] = organisationId;
    $[16] = rememberOrganisation;
    $[17] = scope;
    $[18] = t4;
    $[19] = t5;
  } else {
    t4 = $[18];
    t5 = $[19];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[20] !== setFieldValue) {
    t6 = () => setFieldValue("company_id", null);
    t7 = () => setFieldValue("contact_id", null);
    $[20] = setFieldValue;
    $[21] = t6;
    $[22] = t7;
  } else {
    t6 = $[21];
    t7 = $[22];
  }
  let t8;
  if ($[23] !== companyId || $[24] !== contactId || $[25] !== organisationId || $[26] !== t6 || $[27] !== t7) {
    t8 = {
      companyId,
      contactId,
      isHydrating: false,
      organisationId,
      resetCompany: t6,
      resetContact: t7
    };
    $[23] = companyId;
    $[24] = contactId;
    $[25] = organisationId;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  useResetCustomerSelectionOnOrganisationChange(t8);
  let t9;
  if ($[29] !== setFieldValue) {
    t9 = () => setFieldValue("contact_id", null);
    $[29] = setFieldValue;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== companyId || $[32] !== contactId || $[33] !== t9) {
    t10 = {
      companyId,
      contactId,
      isHydrating: false,
      resetContact: t9
    };
    $[31] = companyId;
    $[32] = contactId;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  useResetContactSelectionOnCompanyChange(t10);
  let t11;
  let t12;
  if ($[35] !== routeDrops || $[36] !== setRouteDrops) {
    t11 = () => {
      setRouteDrops(routeDrops);
    };
    t12 = [routeDrops, setRouteDrops];
    $[35] = routeDrops;
    $[36] = setRouteDrops;
    $[37] = t11;
    $[38] = t12;
  } else {
    t11 = $[37];
    t12 = $[38];
  }
  useEffect(t11, t12);
  let t13;
  if ($[39] !== canSelectOrganisation || $[40] !== companyId || $[41] !== hasOrganisationSelection || $[42] !== inheritedOrganisationId || $[43] !== isAllOrganisationsSelected || $[44] !== localOrgIdOverride) {
    t13 = {
      canSelectOrganisation,
      companyId,
      hasOrganisationSelection,
      inheritedOrganisationId,
      isAllOrganisationsSelected,
      localOrgIdOverride
    };
    $[39] = canSelectOrganisation;
    $[40] = companyId;
    $[41] = hasOrganisationSelection;
    $[42] = inheritedOrganisationId;
    $[43] = isAllOrganisationsSelected;
    $[44] = localOrgIdOverride;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  return t13;
}
function useResetContactSelectionOnCompanyChange(t0) {
  const $ = _c(6);
  const {
    companyId,
    contactId,
    isHydrating,
    resetContact
  } = t0;
  const previousCompanyId = useRef(companyId);
  let t1;
  let t2;
  if ($[0] !== companyId || $[1] !== contactId || $[2] !== isHydrating || $[3] !== resetContact) {
    t1 = () => {
      if (isHydrating) {
        previousCompanyId.current = companyId;
        return;
      }
      if (previousCompanyId.current !== companyId) {
        if (contactId !== null) {
          resetContact();
        }
        previousCompanyId.current = companyId;
      }
    };
    t2 = [companyId, contactId, isHydrating, resetContact];
    $[0] = companyId;
    $[1] = contactId;
    $[2] = isHydrating;
    $[3] = resetContact;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
}
function useResetCustomerSelectionOnOrganisationChange(t0) {
  const $ = _c(8);
  const {
    companyId,
    contactId,
    isHydrating,
    organisationId,
    resetCompany,
    resetContact
  } = t0;
  const previousOrganisationId = useRef(organisationId);
  let t1;
  let t2;
  if ($[0] !== companyId || $[1] !== contactId || $[2] !== isHydrating || $[3] !== organisationId || $[4] !== resetCompany || $[5] !== resetContact) {
    t1 = () => {
      if (isHydrating) {
        previousOrganisationId.current = organisationId;
        return;
      }
      if (previousOrganisationId.current !== organisationId) {
        if (companyId !== null) {
          resetCompany();
        }
        if (contactId !== null) {
          resetContact();
        }
        previousOrganisationId.current = organisationId;
      }
    };
    t2 = [companyId, contactId, isHydrating, organisationId, resetCompany, resetContact];
    $[0] = companyId;
    $[1] = contactId;
    $[2] = isHydrating;
    $[3] = organisationId;
    $[4] = resetCompany;
    $[5] = resetContact;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
}